import { Check, Clock, AlertTriangle } from "lucide-react";
import { MONTHS, SERVICE_TYPES, formatMoney, monthTotals, statusOf, tint } from "@/lib/services";

const MAX_PILLS = 5;

const Totals = ({ list, index }) => {
  const { rows, done, planned, missing } = monthTotals(list);
  if (!rows.length) return null;
  return (
    <div className="mt-3 space-y-1 border-t border-slate-700/50 pt-2 text-[11px]" data-testid={`month-totals-${index + 1}`}>
      {rows.slice(0, 4).map((r) => (
        <div key={r.key} className="flex items-center gap-1.5">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: r.hex }} />
          <span className="min-w-0 flex-1 truncate text-slate-300">{r.label}</span>
          <span className="font-mono text-emerald-300">{formatMoney(r.done)}</span>
          {r.planned > 0 && <span className="font-mono text-slate-500">+{formatMoney(r.planned)}</span>}
        </div>
      ))}
      <div className="flex items-center justify-between pt-1 text-xs font-bold">
        <span className="text-slate-200">Total</span>
        <span className="font-mono"><span className="text-emerald-300" data-testid={`month-total-done-${index + 1}`}>{formatMoney(done)}</span>
          {planned > 0 && <span className="text-slate-400" data-testid={`month-total-planned-${index + 1}`}> · prev. {formatMoney(planned)}</span>}</span>
      </div>
      {missing > 0 && <p className="text-[10px] text-amber-300/90" data-testid={`month-missing-price-${index + 1}`}>{missing} sem valor lançado</p>}
    </div>
  );
};

const Pill = ({ s, today }) => {
  const t = SERVICE_TYPES[s.service_type];
  const st = statusOf(s, today);
  const Icon = st.key === "realizado" ? Check : st.key === "vencido" ? AlertTriangle : Clock;
  const dashed = s.status === "agendado";
  return (
    <div
      className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${dashed ? "border-dashed" : ""} ${st.key === "vencido" ? "ring-1 ring-rose-500/60" : ""}`}
      style={dashed ? { borderColor: `${t.hex}88` } : tint(t.hex)}
      data-testid={`calendar-pill-${s.id}`}
    >
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: t.hex }} />
      <span className="font-mono text-slate-400">{s.execution_date.slice(8, 10)}</span>
      <span className="min-w-0 flex-1 truncate font-medium text-slate-100">{s.client_name}</span>
      <Icon className={`h-3 w-3 shrink-0 ${st.key === "vencido" ? "text-rose-400" : "text-slate-400"}`} />
    </div>
  );
};

const TypeBar = ({ list }) => (
  <div className="mt-3 flex h-1.5 overflow-hidden rounded-full bg-slate-700/60">
    {Object.entries(SERVICE_TYPES).map(([key, t]) => {
      const n = list.filter((s) => s.service_type === key).length;
      return n ? <div key={key} style={{ width: `${(n / list.length) * 100}%`, background: t.hex }} /> : null;
    })}
  </div>
);

const MonthCard = ({ index, list, isCurrent, today, onOpen }) => (
  <button
    onClick={onOpen}
    data-testid={`month-card-${index + 1}`}
    className={`rise flex min-h-[290px] flex-col rounded-xl border bg-slate-800/70 p-4 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-slate-500 ${isCurrent ? "border-slate-200 shadow-[0_0_0_1px_rgba(248,250,252,0.4)]" : "border-slate-700/60"}`}
    style={{ animationDelay: `${index * 35}ms` }}
  >
    <div className="mb-3 flex items-center justify-between border-b border-slate-700/50 pb-3">
      <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-200">
        {MONTHS[index]}
        {isCurrent && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-900">ATUAL</span>}
      </span>
      <span className="rounded-full bg-slate-700 px-2 py-0.5 font-mono text-xs text-slate-300" data-testid={`month-count-${index + 1}`}>{list.length}</span>
    </div>
    <div className="min-h-[150px] flex-1 space-y-1.5 overflow-hidden">
      {list.length === 0 && <p className="pt-6 text-center text-xs text-slate-500">Sem serviços</p>}
      {list.slice(0, MAX_PILLS).map((s) => <Pill key={s.id} s={s} today={today} />)}
      {list.length > MAX_PILLS && <p className="pt-1 text-xs font-medium text-slate-400">+{list.length - MAX_PILLS} mais</p>}
    </div>
    {list.length > 0 && <TypeBar list={list} />}
    <Totals list={list} index={index} />
  </button>
);

export const YearCalendar = ({ year, services, today, onOpenMonth }) => {
  const byMonth = Array.from({ length: 12 }, () => []);
  services.forEach((s) => byMonth[Number(s.execution_date.slice(5, 7)) - 1].push(s));
  const currentIndex = Number(today.slice(0, 4)) === year ? Number(today.slice(5, 7)) - 1 : -1;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="year-calendar">
      {byMonth.map((list, i) => (
        <MonthCard key={`${year}-${i}`} index={i} list={list} isCurrent={i === currentIndex} today={today} onOpen={() => onOpenMonth(i)} />
      ))}
    </div>
  );
};
